import React, { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { CreditCard, LifeBuoy, LogIn } from "lucide-react";
import NotificationBell from "../notifications/NotificationBell";
import { useUser } from "../login/UserContext";
import { openSignInModal } from "../login/signInModalSignal";
import { openSupport } from "../../features/support/supportBus";
import { BOTTOM_NAV_ITEMS, shouldShowBottomNav } from "./navConfig";

/**
 * The phone/tablet bottom bar — the app's primary navigation below 1024px.
 *
 * Five slots, X-style: three product destinations lifted straight out of the
 * rail config (same icons, same short labels), then notifications and help.
 * Signed out, the last two become the two things a visitor actually needs:
 * plans and sign-in.
 *
 * Help lives here rather than in a floating launcher. A permanent button
 * hovering over every page is a lot of screen to spend on a link most members
 * tap once; in the bar it's always visible, never covers content, and reads as
 * part of the app. Desktop gets the same treatment through the rail — both
 * still open the same support sheet.
 *
 * Rendering also sets the `bb-has-bottomnav` flag on <html>, which is what
 * --bb-bottomnav-h keys off; every offset built on that var (page padding, the
 * feed's compose button, the thread reply dock) then follows the bar on and
 * off screen without each surface re-deriving the route rules.
 */
const BottomNav: React.FC = () => {
  const { pathname } = useLocation();
  const { user, authReady } = useUser();
  const isAuthed = authReady && !!user && user.id !== 0;

  const show = shouldShowBottomNav(pathname);

  useEffect(() => {
    document.documentElement.classList.toggle("bb-has-bottomnav", show);
    return () => {
      document.documentElement.classList.remove("bb-has-bottomnav");
    };
  }, [show]);

  if (!show) return null;

  return (
    <nav className="bb-bottomnav" aria-label="Primary">
      {/* No `end`: a detail route keeps its section lit, so opening a post
          still reads as being in the feed (x.com does the same). */}
      {BOTTOM_NAV_ITEMS.map(({ to, short, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          className={({ isActive }) =>
            `bb-bottomnav__item${isActive ? " is-active" : ""}`
          }
        >
          <span className="bb-bottomnav__icon">
            <Icon size={22} strokeWidth={1.9} />
          </span>
          <span className="bb-bottomnav__label">{short}</span>
        </NavLink>
      ))}

      {isAuthed ? (
        <>
          <div className="bb-bottomnav__item bb-bottomnav__item--bell">
            <span className="bb-bottomnav__icon">
              <NotificationBell placement="up" compact />
            </span>
            <span className="bb-bottomnav__label">Alerts</span>
          </div>

          <button
            type="button"
            className="bb-bottomnav__item"
            onClick={() => openSupport()}
          >
            <span className="bb-bottomnav__icon">
              <LifeBuoy size={22} strokeWidth={1.9} />
            </span>
            <span className="bb-bottomnav__label">Help</span>
          </button>
        </>
      ) : (
        <>
          <NavLink
            to="/pricing"
            className={({ isActive }) =>
              `bb-bottomnav__item${isActive ? " is-active" : ""}`
            }
          >
            <span className="bb-bottomnav__icon">
              <CreditCard size={22} strokeWidth={1.9} />
            </span>
            <span className="bb-bottomnav__label">Plans</span>
          </NavLink>

          <button
            type="button"
            className="bb-bottomnav__item"
            onClick={() => openSignInModal()}
          >
            <span className="bb-bottomnav__icon">
              <LogIn size={22} strokeWidth={1.9} />
            </span>
            <span className="bb-bottomnav__label">Sign in</span>
          </button>
        </>
      )}
    </nav>
  );
};

export default BottomNav;
